// DeploymentDecisionsPanel — Phase 2 Day 3 of
// plan/audits/Deployment_Manifest_Resolution_Hierarchy_Audit_2026-05-28.md.
//
// G3.7 manifest-review surface. Renders the 24 deployment-manifest
// decisions as a colour-coded table; each row is one click away
// from an override modal whose flavor (soft / mandatory) is
// driven by `underlying_rule_kind`. The structural discipline
// floor (mandatory overrides require evidence) is enforced both
// at the model layer AND at the modal — the architect cannot
// silently skip evidence on a compliance-cited key.
//
// Layout:
//
//   ┌──────────────────────────────────────────────────────────┐
//   │ Header: catalog versions, override count, TBD count       │
//   ├──────────────────────────────────────────────────────────┤
//   │ Decisions table (24 rows × KEY · VALUE · BASIS · ACTION) │
//   ├──────────────────────────────────────────────────────────┤
//   │ Override summary (when overrides exist)                   │
//   └──────────────────────────────────────────────────────────┘
//
// The override modal is rendered as a portal sibling. The summary
// card sits below the table when override_count > 0 and links
// each override to its Appendix C anchor (rendered by the
// exporter at P5).

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Pencil,
  ShieldCheck,
  X,
} from 'lucide-react'


import {
  createOrReplaceOverride,
  getDecisionResolutions,
} from '@/lib/overridesApi'
import {
  BASIS_PALETTE,
  type DecisionRow,
  type DecisionResolutionsResponse,
  type OverrideCategory,
  type OverrideRequest,
  type RuleKind,
} from '@/types/overrides'

// --------------------------------------------------------------------------- //
// Helpers
// --------------------------------------------------------------------------- //
function basisChip(row: DecisionRow): { label: string; class: string } {
  const palette = row.decision_basis
    ? BASIS_PALETTE[row.decision_basis]
    : BASIS_PALETTE.tbd
  return { label: palette.label, class: palette.chipClass }
}

function isPlaceholderValue(value: string): boolean {
  const v = value.trim().toUpperCase()
  return v === 'TBD' || v === 'CONDITIONAL-TBD' || v === '<LLM-DEFAULT-PENDING>'
}

function inferModalFlavor(row: DecisionRow): RuleKind {
  // When the row is overridden, the shadow records the original
  // rule_kind; the modal stays in mandatory flavor so the architect
  // can revise their evidence.
  if (row.underlying_rule_kind) {
    return row.underlying_rule_kind
  }
  // For non-overridden rows, infer from the basis: profile-derived
  // + intake + srd-derived keys are mandatory-equivalent;
  // mechanical + llm_default + conditional + tbd are default.
  switch (row.decision_basis) {
    case 'profile_derived':
    case 'intake':
    case 'srd_derived':
      return 'mandatory'
    default:
      return 'default'
  }
}

// --------------------------------------------------------------------------- //
// Override modal — soft + mandatory flavors
// --------------------------------------------------------------------------- //
interface OverrideModalProps {
  row: DecisionRow
  onClose: () => void
  onSubmit: (body: OverrideRequest) => void
  pending: boolean
  error?: string | null
}

function OverrideModal({
  row,
  onClose,
  onSubmit,
  pending,
  error,
}: OverrideModalProps) {
  const flavor = inferModalFlavor(row)
  const [value, setValue] = useState<string>(
    isPlaceholderValue(row.decision) ? '' : row.decision,
  )
  const [category, setCategory] = useState<OverrideCategory>(
    flavor === 'mandatory' ? 'regulator_approved_exception' : 'soft',
  )
  const [rationale, setRationale] = useState<string>('')
  const [approverName, setApproverName] = useState<string>('')
  const [approvalDate, setApprovalDate] = useState<string>('')
  const [reference, setReference] = useState<string>('')
  const [evidencePath, setEvidencePath] = useState<string>('')
  const [acknowledged, setAcknowledged] = useState<boolean>(false)

  const requiresEvidence = category === 'regulator_approved_exception'

  const canSubmit = useMemo(() => {
    if (!value.trim() || !rationale.trim()) return false
    if (flavor === 'mandatory' && !acknowledged) return false
    if (requiresEvidence) {
      if (
        !approverName.trim() ||
        !approvalDate.trim() ||
        !reference.trim() ||
        !evidencePath.trim()
      )
        return false
    }
    return true
  }, [
    value,
    rationale,
    flavor,
    acknowledged,
    requiresEvidence,
    approverName,
    approvalDate,
    reference,
    evidencePath,
  ])

  function handleSubmit() {
    const body: OverrideRequest = {
      key: row.key,
      to_value: value.trim(),
      category,
      rationale: rationale.trim(),
    }
    if (requiresEvidence) {
      body.evidence = {
        approver_name: approverName.trim(),
        approval_date: approvalDate.trim(),
        reference: reference.trim(),
        evidence_path: evidencePath.trim(),
      }
    }
    onSubmit(body)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-[640px] overflow-y-auto rounded-lg border border-zinc-700 bg-zinc-900 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-xs font-mono text-zinc-400">{row.key}</div>
            <h2 className="text-lg font-semibold text-zinc-100">
              {flavor === 'mandatory' ? (
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-amber-400" />
                  Override mandatory decision
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Pencil className="h-5 w-5 text-sky-400" />
                  Override default decision
                </span>
              )}
            </h2>
            <p className="mt-1 text-sm text-zinc-400">
              {flavor === 'mandatory'
                ? "This key derives from a compliance-cited rule. Override requires regulator-approved evidence; PEG-OVERRIDE-EVIDENCE-001 blocks export without it."
                : "Soft override. A one-sentence rationale is required for the audit trail."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Shadow context — what the system would have resolved without override */}
        {(row.decision_basis !== 'architect_override' && row.decision_basis) && (
          <div className="mb-4 rounded border border-zinc-800 bg-zinc-950/50 p-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-zinc-500">
              System resolution (what we'd ship without override)
            </div>
            <div className="mt-1 font-mono text-zinc-200">
              {row.decision || '—'}
            </div>
            {row.cites.length > 0 && (
              <div className="mt-1 text-xs text-zinc-400">
                cite: {row.cites[0]}
              </div>
            )}
          </div>
        )}

        {row.shadow_resolution && (
          <div className="mb-4 rounded border border-zinc-800 bg-zinc-950/50 p-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-zinc-500">
              Currently overridden — would have been
            </div>
            <div className="mt-1 font-mono text-zinc-200">
              {row.shadow_resolution.would_have_resolved_to}
            </div>
            {row.shadow_resolution.would_have_cites.length > 0 && (
              <div className="mt-1 text-xs text-zinc-400">
                cite: {row.shadow_resolution.would_have_cites[0]}
              </div>
            )}
          </div>
        )}

        {/* New value */}
        <label className="mb-1 block text-sm font-medium text-zinc-300">
          New value
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mb-4 w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-sky-600 focus:outline-none"
          placeholder="e.g. '6 months'"
        />

        {/* Category — only the mandatory flavor shows the radio */}
        {flavor === 'mandatory' && (
          <div className="mb-4">
            <div className="mb-2 text-sm font-medium text-zinc-300">
              Category
            </div>
            <div className="space-y-2 text-sm">
              {(
                [
                  {
                    value: 'regulator_approved_exception' as OverrideCategory,
                    label: 'Regulator-approved exception',
                    description:
                      "QSA / regulator / AO has issued formal approval. Requires structured evidence.",
                  },
                  {
                    value: 'risk_accepted' as OverrideCategory,
                    label: 'Risk accepted',
                    description:
                      "Architect explicitly accepts the deviation risk. No regulator sign-off claimed.",
                  },
                  {
                    value: 'technical_constraint_documented' as OverrideCategory,
                    label: 'Technical constraint',
                    description:
                      "The rule's value is technically impossible in this environment.",
                  },
                ] as const
              ).map((opt) => (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-start gap-2 rounded border border-zinc-800 p-2 hover:bg-zinc-800/40"
                >
                  <input
                    type="radio"
                    name="override-category"
                    value={opt.value}
                    checked={category === opt.value}
                    onChange={() => setCategory(opt.value)}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-zinc-100">{opt.label}</div>
                    <div className="text-xs text-zinc-400">{opt.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Evidence block — only when category requires it */}
        {requiresEvidence && (
          <div className="mb-4 rounded border border-amber-900/50 bg-amber-950/20 p-3">
            <div className="mb-2 text-xs uppercase tracking-wide text-amber-300">
              Evidence (required)
            </div>
            <div className="space-y-2">
              <input
                type="text"
                value={approverName}
                onChange={(e) => setApproverName(e.target.value)}
                placeholder="Approver (e.g. 'Acme Compliance Inc. (QSA)')"
                className="w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
              />
              <input
                type="date"
                value={approvalDate}
                onChange={(e) => setApprovalDate(e.target.value)}
                className="w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
              />
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Reference (e.g. 'QSA-EXCEPTION-XYZ-2026-03')"
                className="w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
              />
              <input
                type="text"
                value={evidencePath}
                onChange={(e) => setEvidencePath(e.target.value)}
                placeholder="Evidence path (e.g. 'evidence/qsa-letter.pdf' or URL)"
                className="w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
              />
            </div>
          </div>
        )}

        {/* Rationale */}
        <label className="mb-1 block text-sm font-medium text-zinc-300">
          Rationale {flavor === 'mandatory' && '(3+ sentences recommended)'}
        </label>
        <textarea
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          rows={flavor === 'mandatory' ? 4 : 2}
          className="mb-4 w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-sky-600 focus:outline-none"
          placeholder={
            flavor === 'mandatory'
              ? "Explain the regulator's reasoning and what compensating control is in place."
              : "Why this override?"
          }
        />

        {/* Acknowledgment — mandatory flavor only */}
        {flavor === 'mandatory' && (
          <label className="mb-4 flex items-start gap-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1"
            />
            <span>
              I understand this overrides{' '}
              {row.shadow_resolution?.would_have_cites[0] ||
                (row.cites[0] ?? 'a compliance-cited rule')}{' '}
              and that the rationale + evidence will appear in the
              shipped audit package.
            </span>
          </label>
        )}

        {error && (
          <div className="mb-3 flex items-start gap-2 rounded border border-red-800 bg-red-950/40 p-2 text-sm text-red-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-zinc-700 px-3 py-1.5 text-sm text-zinc-200 hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || pending}
            className="flex items-center gap-2 rounded bg-sky-600 px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 hover:bg-sky-500"
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {flavor === 'mandatory' ? 'Submit override' : 'Apply override'}
          </button>
        </div>
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Summary card — shown below the table when overrides exist
// --------------------------------------------------------------------------- //
function OverrideSummaryCard({ rows }: { rows: DecisionRow[] }) {
  const overrides = rows.filter((r) => r.is_overridden)
  if (overrides.length === 0) return null
  return (
    <div className="mt-4 rounded border border-purple-800/50 bg-purple-950/20 p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-purple-200">
        <ShieldCheck className="h-4 w-4" />
        {overrides.length} architect override
        {overrides.length === 1 ? '' : 's'}
      </div>
      <ul className="space-y-1 text-xs">
        {overrides.map((r, idx) => (
          <li key={r.key} className="flex items-center gap-2 font-mono text-purple-200">
            <span className="text-purple-400">C.{idx + 1}</span>
            <span>{r.key}</span>
            <span className="text-zinc-500">→</span>
            <span>{r.decision}</span>
            {r.shadow_resolution && (
              <>
                <span className="text-zinc-600">(was</span>
                <span className="text-zinc-400 line-through">
                  {r.shadow_resolution.would_have_resolved_to}
                </span>
                <span className="text-zinc-600">)</span>
              </>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-2 text-xs text-purple-300/70">
        These render as inline footnotes + Appendix C in the exported requirements doc.
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Section chrome — the panel owns its own header + source caption so
// that when the resolver is disabled the WHOLE section (header
// included) disappears instead of leaving an empty "Deployment
// Decisions" heading above an operator-only message the end user
// can't act on.
// --------------------------------------------------------------------------- //
function DeploymentDecisionsSection({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <div className="mt-4 rounded-lg border border-slate-700/60 bg-slate-950/40 p-3">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-200">
          Deployment Decisions
        </h3>
        <span className="text-xs text-slate-500">
          phase3/deployment-manifest.json
        </span>
      </div>
      {children}
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Main panel
// --------------------------------------------------------------------------- //
export interface DeploymentDecisionsPanelProps {
  projectId: string
  runId: number
}


export function DeploymentDecisionsPanel({
  projectId,
  runId,
}: DeploymentDecisionsPanelProps) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<DecisionRow | null>(null)
  const [modalError, setModalError] = useState<string | null>(null)

  const queryKey = ['decision-resolutions', projectId, runId]
  const { data, isLoading, error } = useQuery<DecisionResolutionsResponse>({
    queryKey,
    queryFn: () => getDecisionResolutions(projectId, runId),
  })

  const mutation = useMutation({
    mutationFn: (body: OverrideRequest) =>
      createOrReplaceOverride(projectId, runId, body),
    onSuccess: (resp) => {
      // Use the fresh snapshot the server returned to seed the cache
      // — avoids an extra GET round-trip.
      queryClient.setQueryData(queryKey, resp.decisions)
      setEditing(null)
      setModalError(null)
    },
    onError: (err: Error) => {
      setModalError(err.message)
    },
  })

  if (isLoading) {
    return (
      <DeploymentDecisionsSection>
        <div className="flex items-center gap-2 p-4 text-sm text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading deployment decisions…
        </div>
      </DeploymentDecisionsSection>
    )
  }


  if (error) {
    return (
      <DeploymentDecisionsSection>
        <div className="rounded border border-red-800 bg-red-950/30 p-3 text-sm text-red-200">
          Failed to load decisions: {String((error as Error).message)}
        </div>
      </DeploymentDecisionsSection>
    )
  }


  if (!data) return null

  // When the optional decision-resolver feature isn't enabled for
  // this workspace there is nothing for the architect to act on —
  // the deployment manifest still ships as part of the requirements
  // package. Render nothing rather than surfacing an operator-only
  // toggle the end user can neither understand nor change (UAT
  // 2026-06-13: "speaks of options the end user could not possibly
  // understand or change"). ``ContentAwareG37Gate`` hides the whole
  // section header when this returns null.
  if (!data.resolver_enabled) {
    return null
  }


  const rows = data.decisions
  if (rows.length === 0) {
    return (
      <DeploymentDecisionsSection>
        <div className="rounded border border-zinc-700 bg-zinc-900/50 p-4 text-sm text-zinc-400">
          No deployment-manifest decisions resolved yet. The
          Operations Architect (Phase 3) hasn't run, or the manifest
          is missing.
        </div>
      </DeploymentDecisionsSection>
    )
  }

  return (
    <DeploymentDecisionsSection>
    <div className="space-y-4">

      {/* Header */}
      <div className="flex flex-wrap items-baseline gap-3 text-xs text-zinc-400">
        {data.catalog_versions?.regulatory_profiles && (
          <span>
            regulatory_profiles v
            {data.catalog_versions.regulatory_profiles}
          </span>
        )}
        {data.catalog_versions?.shape_profiles && (
          <span>shape_profiles v{data.catalog_versions.shape_profiles}</span>
        )}
        {data.catalog_versions?.mechanical_derivations && (
          <span>
            mechanical v{data.catalog_versions.mechanical_derivations}
          </span>
        )}
        {data.override_count > 0 && (
          <span className="text-purple-300">
            {data.override_count} override
            {data.override_count === 1 ? '' : 's'}
          </span>
        )}
        {data.last_resort_tbd_keys.length > 0 && (
          <span className="text-red-300">
            {data.last_resort_tbd_keys.length} TBD
            {data.last_resort_tbd_keys.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/70 text-xs uppercase tracking-wide text-zinc-400">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Key</th>
              <th className="px-3 py-2 text-left font-medium">Value</th>
              <th className="px-3 py-2 text-left font-medium">Basis</th>
              <th className="px-3 py-2 text-left font-medium">Cite</th>
              <th className="px-3 py-2 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {rows.map((row) => {
              const chip = basisChip(row)
              const rowBg = row.decision_basis
                ? BASIS_PALETTE[row.decision_basis].bgClass
                : ''
              return (
                <tr key={row.key} className={`bg-zinc-900/40 ${rowBg}`}>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-300">
                    {row.key}
                  </td>
                  <td className="px-3 py-2 text-zinc-100">
                    <div className="max-w-[300px] truncate">{row.decision}</div>
                    {row.is_overridden && row.shadow_resolution && (
                      <div className="mt-0.5 text-xs text-zinc-500">
                        was:{' '}
                        <span className="line-through">
                          {row.shadow_resolution.would_have_resolved_to}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium ${chip.class}`}
                    >
                      {chip.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-400">
                    <div className="max-w-[240px] truncate">
                      {row.cites[0] ?? '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => {
                        setEditing(row)
                        setModalError(null)
                      }}
                      className="inline-flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-xs text-zinc-200 hover:bg-zinc-800 hover:text-white"
                    >
                      <Pencil className="h-3 w-3" />
                      {row.is_overridden ? 'Edit override' : 'Override'}
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <OverrideSummaryCard rows={rows} />

      {/* Help footer */}
      <div className="rounded border border-zinc-800 bg-zinc-900/40 p-3 text-xs text-zinc-400">
        <div className="mb-1 flex items-center gap-2 text-zinc-300">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          What does basis mean?
        </div>
        <p>
          Each decision is tagged with how the resolver chose its
          value. <span className="text-emerald-200">Intake</span> +{' '}
          <span className="text-emerald-200">Profile</span> are
          authoritative (compliance- or architect-pinned).{' '}
          <span className="text-sky-200">Mechanical</span> +{' '}
          <span className="text-sky-200">SRD</span> are deterministic
          derivations.{' '}
          <span className="text-amber-200">LLM-default</span> is the
          model's reasoned guess — accept or override.{' '}
          <span className="text-zinc-300">Conditional</span> defers
          until a precondition is met.{' '}
          <span className="text-red-200">TBD</span> means no rule
          fired; the architect must act.{' '}
          <span className="text-purple-200">Override</span> is an
          architect decision that supersedes the system's choice;
          PEG-OVERRIDE-EVIDENCE-001 validates the supporting evidence
          at export.
        </p>
      </div>

      {editing && (
        <OverrideModal
          row={editing}
          onClose={() => {
            setEditing(null)
            setModalError(null)
          }}
          onSubmit={(body) => mutation.mutate(body)}
          pending={mutation.isPending}
          error={modalError}
        />
      )}
    </div>
    </DeploymentDecisionsSection>
  )
}

